import { useCallback, useState } from 'react';
import { AgenciesService } from '@services/requests/housien-services/AgenciesService';
import { refusalOf, type PreviewRefusal } from './previewRefusal';
import type {
  ChangePlanPreviewRequest,
  ChangePlanPreviewResponse,
} from '@services/requests/housien-services/AgenciesService.subscription';

export interface UseChangePlanPreviewResult {
  fetchPreview: (req: ChangePlanPreviewRequest) => Promise<void>;
  preview: ChangePlanPreviewResponse | null;
  isLoading: boolean;
  isError: boolean;
  /** The server said no and why; a retry cannot change that. */
  refusal: PreviewRefusal | null;
  reset: () => void;
}

/**
 * Fetches the exact pro-rata amount for a plan upgrade without creating a charge.
 *
 * Calls POST /agencies/{agencyId}/subscription/change-plan/preview and exposes
 * isLoading (disables confirm button while in-flight) and isError (disables
 * confirm button and shows error message on failure).
 */
export function useChangePlanPreview(agencyId: string): UseChangePlanPreviewResult {
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<ChangePlanPreviewResponse | null>(null);
  const [isError, setIsError] = useState(false);
  const [refusal, setRefusal] = useState<PreviewRefusal | null>(null);

  const fetchPreview = useCallback(
    async (req: ChangePlanPreviewRequest) => {
      setIsLoading(true);
      setIsError(false);
      setRefusal(null);
      setPreview(null);
      try {
        const response = await AgenciesService.previewChangePlan(agencyId, req);
        setPreview(response);
      } catch (error) {
        const reason = refusalOf(error);
        setRefusal(reason);
        setIsError(reason === null);
      } finally {
        setIsLoading(false);
      }
    },
    [agencyId],
  );

  const reset = useCallback(() => {
    setPreview(null);
    setIsError(false);
    setRefusal(null);
  }, []);

  return { fetchPreview, preview, isLoading, isError, refusal, reset };
}
